import PropTypes from "prop-types";

import { Pagination } from "@components/pagination";

import { apiApolloFetch } from "@queries/apiApolloFetch";
import CITIES_BY_COUNTRY from "@queries/sitemap/cities.gql";

import SitemapPage, { breadcrumbs } from "../index.page";

export default function SitemapDirectoryCities({ metadata, items }) {
  return (
    <SitemapPage metadata={metadata}>
      <a href={`/destinations/${metadata.country.slug}`} className="text-blue">
        Learn more about {metadata.country.label}
      </a>
      <ul className="my-8 grid grid-cols-3 gap-2">
        {items.map(item => (
          <li key={item.slug}>
            <a
              href={`/destinations/${item.slug}`}
              className="text-sm text-slate hover:text-black"
            >
              {item.title}
            </a>
          </li>
        ))}
      </ul>

      {metadata.pages > 1 && (
        <div className="sticky bottom-0 bg-white divide-none py-2">
          <Pagination
            formSlug={metadata.country.slug}
            selectedPage={metadata.page}
            numPages={metadata.pages}
          />
        </div>
      )}
    </SitemapPage>
  );
}

export async function getServerSideProps({ query, resolvedUrl }) {
  const { country, page = 1 } = query;
  const pagesize = 100;

  /**
   * There is a potential DDoS attack vector where endlessly increasing
   * the page param's value on request will fire expensive queries
   * indefinately. There isn't a straight forward way to set a dynamic
   * maximum per request type but we can generally know how many pages
   * of content we have, so we will set that  soft cap as a means to
   * mitigate the issue by not allowing _enough_ range to effectively
   * tip-over the servers.
   */

  /* eslint-disable camelcase */
  const magic_page_limit = 10;
  if (page > magic_page_limit || Number.isNaN(Number(page))) {
    return {
      notFound: true,
    };
  }

  const { data } = await apiApolloFetch({
    context: {
      uri: `${process.env.NEXT_PUBLIC_LPAPI}/consumer/graphql/v1`,
      headers: {
        "x-api-key": process.env.NEXT_PUBLIC_LPAPI_KEY,
      },
    },
    query: CITIES_BY_COUNTRY,
    variables: {
      country,
      offset: (page - 1) * pagesize || 0,
    },
  });

  const { count } = data.places;

  return {
    props: {
      metadata: {
        title: `Explore ${data.place.title}`,
        breadcrumbs: breadcrumbs(resolvedUrl),
        country: {
          label: data.place.title,
          slug: data.place.slug,
        },
        page: Number(page) || 1,
        pages: Math.floor(count / pagesize) + 1,
      },
      items: data.places.items,
    },
  };
}

SitemapDirectoryCities.propTypes = {
  metadata: PropTypes.shape({
    title: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired,
      })
    ),
    // local
    country: PropTypes.shape({
      label: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
    }).isRequired,
    page: PropTypes.number.isRequired,
    pages: PropTypes.number.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
};
